import React, { useRef, useState } from 'react';
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/SimpleAuthContext';
import API_CONFIG from '../../config/apiConfig';
import { useToast } from '../../contexts/ToastContext';
import './DocumentUploadModal.css';

const DocumentUploadModal = ({ isOpen, onClose, registrationId, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState('other');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { makeAuthenticatedRequest } = useAuth();
  const { showToast } = useToast();

  if (!isOpen) return null;

  const selectFile = (selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleFileChange = (e) => {
    selectFile(e.target.files?.[0]);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    selectFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      showToast('Please select a file', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('registration', registrationId);
    formData.append('document_type', documentType);
    formData.append('file', file);
    formData.append('title', file.name);

    try {
      // The content-type header is usually set automatically by the browser when using FormData
      // but simpleAuthContext might override headers. 
      // We pass 'undefined' for Content-Type to let browser set it with boundary
      const response = await makeAuthenticatedRequest(API_CONFIG.registrations.documents(), {
        method: 'POST',
        body: formData,
        headers: {
            // Explicitly removing Content-Type so browser sets it with boundary
        }
      });
      
      // Note: simpleAuthContext usually sets 'Content-Type': 'application/json' by default.
      // We might need to handle this. If makeAuthenticatedRequest merges headers, we need to override.
      // Let's assume makeAuthenticatedRequest is smart enough or we check its implementation.
      // For now, let's just try. If it fails, I'll fix it.

      if (response.ok) {
        showToast('Document uploaded successfully', 'success');
        if (onUploadSuccess) onUploadSuccess();
        onClose();
        setFile(null);
      } else {
        const errorData = await response.json();
        showToast(errorData.error || 'Failed to upload document', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('An error occurred during upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="document-upload-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-upload-title"
    >
      <div className="document-upload-dialog">
          <div className="document-upload-header">
            <div>
              <p className="document-upload-eyebrow">
                Registration Documents
              </p>
              <h3 id="document-upload-title" className="document-upload-title">
                Upload Document
              </h3>
            </div>
            <button
              onClick={onClose}
              className="document-upload-close"
              aria-label="Close upload dialog"
            >
              <XMarkIcon className="document-upload-icon" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="document-upload-form">
              <div className="document-upload-field">
                <label className="document-upload-label" htmlFor="document-type">
                  Document Type
                </label>
                <select
                  id="document-type"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="document-upload-select"
                >
                  <option value="id_document">ID Document</option>
                  <option value="matric_certificate">Matric Certificate</option>
                  <option value="qualification_certificate">Qualification Certificate</option>
                  <option value="cv">Curriculum Vitae</option>
                  <option value="motivation_letter">Motivation Letter</option>
                  <option value="medical_certificate">Medical Certificate</option>
                  <option value="proof_of_residence">Proof of Residence</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="document-upload-field">
                <label className="document-upload-label" htmlFor="file-upload">
                  File
                </label>
                <button
                  type="button"
                  onClick={openFilePicker}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  className="document-upload-dropzone"
                >
                  <span className="document-upload-icon-wrap" aria-hidden="true">
                    <CloudArrowUpIcon className="document-upload-cloud-icon" />
                  </span>
                  <span className="document-upload-prompt">
                    Upload a file or drag and drop
                  </span>
                  <span className="document-upload-hint">
                    PDF, PNG, JPG up to 10MB
                  </span>
                  {file ? (
                    <span className="document-upload-file">
                      {file.name}
                    </span>
                  ) : (
                    <span className="document-upload-choose">
                      Choose a file
                    </span>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  className="document-upload-hidden-input"
                  onChange={handleFileChange}
                />
              </div>
            <div className="document-upload-actions">
              <button
                type="button"
                onClick={onClose}
                className="document-upload-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="document-upload-submit"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
